import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { PLATFORM_ASSISTANT_SLUG, type ConversationDTO } from "../../shared/types";
import { ApiError, get, post } from "../lib/api";
import { useConversation } from "../lib/useConversation";
import { TextLink } from "./TextLink";
import { Transcript } from "./Transcript";
import { Button, timeAgo } from "./ui";

const STORAGE_KEY = "pandas.platformAssistant.conversation";

const EXAMPLES = [
  "Create an agent called Legal Reviewer that reviews contracts, using claude-opus-5",
  "Which agents have no connectors, and who can use each agent?",
  "Give every member of the Sales team access to the Sales agent",
  "Add a pinned global memory with our support hours: Mon–Fri 9:00–18:00 CET",
];

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};
const store = (id: string | undefined) => {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable
  }
};

/** Floating chat with the admin-only platform assistant, available on every admin page. */
export function PlatformAssistant() {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(readStored);
  const [conversations, setConversations] = useState<ConversationDTO[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const state = useConversation(open ? conversationId : undefined);
  const active = Object.values(state.runs).some((r) => r.status === "queued" || r.status === "running");

  useEffect(() => {
    if (!open) return;
    // One chat per agent: a remembered id that is no longer the live one (an earlier chat) is dropped.
    void get<ConversationDTO[]>(`/api/conversations?agent=${PLATFORM_ASSISTANT_SLUG}`).then((list) => {
      setConversations(list);
      if (conversationId && !list.some((c) => c.id === conversationId)) select(list[0]?.id);
    }, () => {});
    textarea.current?.focus();
  }, [open, conversationId]);

  useEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  const select = (id: string | undefined) => {
    setConversationId(id);
    store(id);
    setError(null);
  };

  async function send() {
    if (!text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await post<{ conversation: ConversationDTO }>("/api/chat", { conversationId, text: text.trim(), agent: PLATFORM_ASSISTANT_SLUG });
      setText("");
      if (res.conversation.id !== conversationId) select(res.conversation.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send");
    } finally {
      setSending(false);
      textarea.current?.focus();
    }
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open platform assistant"
          title="Platform assistant"
          className="fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 flex size-14 items-center justify-center rounded-full bg-ink text-2xl text-paper shadow-lg transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 dark:bg-white dark:text-ink"
        >
          <Icon name="agents" className="size-6" />
        </button>
      )}
      {open && (
        <div
          role="dialog"
          aria-label="Platform assistant"
          className="fixed inset-0 z-50 flex flex-col bg-surface shadow-2xl md:inset-auto md:right-4 md:bottom-4 md:h-[min(40rem,calc(100dvh-5rem))] md:w-[28rem] md:rounded-2xl md:border md:border-line"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2.5">
            <Icon name="agents" className="size-5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Platform assistant</div>
              <div className="truncate text-tiny text-muted">Manages agents, users, access and data. Changes are audited as you.</div>
            </div>
            <select
              aria-label="Previous chats"
              className="max-w-[8rem] rounded border border-line bg-transparent px-1 py-1 text-xs"
              value={conversationId ?? ""}
              onChange={(e) => select(e.target.value || undefined)}
            >
              <option value="">New chat</option>
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title.slice(0, 40)} · {timeAgo(c.updatedAt)}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={() => select(undefined)} title="New chat">
              New
            </Button>
            <IconButton size="sm" fill="none" label="Close" onClick={() => setOpen(false)}>
              <Icon name="close" className="size-4" />
            </IconButton>
          </div>

          <div className="scroll-thin min-h-0 flex-1 overflow-auto text-sm">
            {!conversationId ? (
              <div className="space-y-2 p-4">
                <p className="text-muted">Ask for changes to platform data in plain words. The assistant reads the current state first and asks before deleting or changing access.</p>
                {EXAMPLES.map((example) => (
                  <button
                    key={example}
                    onClick={() => {
                      setText(example);
                      textarea.current?.focus();
                    }}
                    className="block w-full rounded-lg border border-line px-3 py-2 text-left text-xs hover:bg-fill-subtle"
                  >
                    {example}
                  </button>
                ))}
              </div>
            ) : state.loading ? (
              <p className="p-4 text-muted">Loading…</p>
            ) : state.error ? (
              <div className="p-4 text-danger">
                {state.error}{" "}
                <TextLink onClick={() => select(undefined)}>Start a new chat</TextLink>
              </div>
            ) : (
              <Transcript state={state} />
            )}
          </div>

          <div className="shrink-0 border-t border-line p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {error && <p className="px-2 pb-1 text-xs text-danger">{error}</p>}
            <form
              className="flex items-end gap-1 rounded-2xl border border-line p-1 focus-within:border-line"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <textarea
                ref={textarea}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void send();
                  }
                }}
                placeholder="Ask the platform assistant"
                aria-label="Message the platform assistant"
                className="scroll-thin max-h-40 min-w-0 flex-1 resize-none self-center bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-faint"
              />
              {active && conversationId && !text.trim() ? (
                <Button variant="primary" size="sm" type="button" onClick={() => post(`/api/conversations/${conversationId}/stop`)}>
                  Stop
                </Button>
              ) : (
                <Button variant="primary" size="sm" type="submit" disabled={!text.trim() || sending}>
                  {sending ? "…" : "Send"}
                </Button>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}
